import { clubStorage } from './club-session';
/**
 * Economy & Stadium State (Microservicio de Economía, Estadio e Infraestructura)
 * Gestiona ampliación del estadio, módulos comerciales (parking, cafetería, tienda),
 * cuerpo técnico (staff), préstamos bancarios y precios de abonos y taquilla.
 */
import { Injectable, inject, signal, computed } from '@angular/core';
import {
  LedgerEntry,
  BankLoan,
  BackendStadiumResponse,
  BackendStaffMember,
  BackendBalanceResponse,
  BackendLedgerResponse,
  Club,
  StadiumCatalogResponse,
  ConstructionStatus,
  StadiumUpgradeQuoteResponse,
  UpgradeOperation,
  StadiumUpgradeModule,
  BackendTicketPriceResponse,
  SponsorMarketOffer,
  SponsorContract,
  SponsorType,
  MatchdayLoanOffer,
  ActiveMatchdayLoan,
  MatchdayLoanItem,
  AggregatedDebtResponse,
  EconomicSummaryResponse,
} from '../models/types';
import {
  STADIUM_TIERS,
  COMMERCIAL_MODULE_COSTS,
} from '../simulation/simulation';
import { INITIAL_USER_CLUB } from '../../../data/initialData';
import { UiState } from './ui.state';
import { SquadState } from './squad.state';
import { clubsApi } from '../http/clubs.api';
import { stadiumEconomyApi } from '../http/stadium-economy.api';
import { stadiumUpgradesApi } from '../http/stadium-upgrades.api';
import { sponsorsApi } from '../http/sponsors.api';
import { loansApi } from '../http/loans.api';

const STORAGE_KEY = 'football_manager_real_v01';

@Injectable({
  providedIn: 'root',
})
export class EconomyState {
  private readonly ui = inject(UiState);
  private readonly squad = inject(SquadState);

  // Signals for backend state & loading flags
  readonly stadiumData = signal<BackendStadiumResponse | null>(null);
  readonly staffData = signal<BackendStaffMember[] | null>(null);
  readonly balanceData = signal<BackendBalanceResponse | null>(null);
  readonly ledgerData = signal<BackendLedgerResponse | null>(null);

  readonly isStadiumLoading = signal<boolean>(false);
  readonly isStaffLoading = signal<boolean>(false);
  readonly isEconomyLoading = signal<boolean>(false);

  private stadiumLoadedAt = 0;
  private ticketsLoadedAt = 0;
  private contractsLoadedAt = 0;
  private contractsClubId: string | null = null;
  private isFresh(timestamp: number): boolean {
    return timestamp > 0 && new Date(timestamp).toDateString() === new Date(Date.now()).toDateString();
  }

  // Cache tracker to avoid redundant backend calls
  readonly loadedStadiumClubId = signal<string | null>(null);
  readonly loadedStaffClubId = signal<string | null>(null);
  readonly loadedEconomyClubId = signal<string | null>(null);
  readonly loadedBalanceClubId = signal<string | null>(null);

  // Signals for stadium remodelations & construction
  readonly catalogData = signal<StadiumCatalogResponse | null>(null);
  readonly constructionData = signal<ConstructionStatus | null>(null);
  readonly isCatalogLoading = signal<boolean>(false);
  readonly isConstructionLoading = signal<boolean>(false);
  readonly loadedCatalogClubId = signal<string | null>(null);
  readonly loadedConstructionClubId = signal<string | null>(null);

  // Signal for ticket pricing (from GET /api/clubs/{clubId}/stadium/tickets)
  readonly ticketPriceData = signal<BackendTicketPriceResponse | null>(null);
  readonly isTicketPriceLoading = signal<boolean>(false);
  readonly loadedTicketPriceClubId = signal<string | null>(null);

  // Signals for sponsors (from /api/clubs/{clubId}/economy/sponsors/*)
  readonly sponsorOffers = signal<SponsorMarketOffer[]>([]);
  readonly sponsorContracts = signal<SponsorContract[]>([]);
  readonly isSponsorOffersLoading = signal<boolean>(false);
  readonly isSponsorContractsLoading = signal<boolean>(false);
  readonly sponsorOffersError = signal<string | null>(null);
  readonly sponsorOffersStatus = signal<number | null>(null);
  readonly sponsorContractsError = signal<string | null>(null);
  readonly loadedSponsorsClubId = signal<string | null>(null);

  readonly activeSponsorContracts = computed<SponsorContract[]>(() =>
    this.sponsorContracts().filter(
      (c) => c.status === 'ACTIVE' || c.status === 'PENDING_ACTIVATION'
    )
  );

  readonly occupiedSponsorTypes = computed<Set<SponsorType>>(() => {
    const set = new Set<SponsorType>();
    for (const c of this.activeSponsorContracts()) {
      if (c.type) {
        set.add(c.type);
      }
    }
    return set;
  });

  // Signals for matchday loans (from /api/clubs/{clubId}/economy/loans/*)
  readonly matchdayLoanOffer = signal<MatchdayLoanOffer | null>(null);
  readonly activeMatchdayLoan = signal<ActiveMatchdayLoan | null>(null);
  readonly matchdayLoansHistory = signal<MatchdayLoanItem[]>([]);
  readonly aggregatedDebt = signal<AggregatedDebtResponse | null>(null);
  readonly economicSummary = signal<EconomicSummaryResponse | null>(null);
  readonly isLoansLoading = signal<boolean>(false);
  readonly loansError = signal<string | null>(null);
  readonly loadedLoansClubId = signal<string | null>(null);

  // In-flight promises to deduplicate concurrent backend calls
  private inFlightStadium: Promise<BackendStadiumResponse | null> | null = null;
  private inFlightStaff: Promise<BackendStaffMember[] | null> | null = null;
  private inFlightEconomy: Promise<{ balance: BackendBalanceResponse | null; ledger: BackendLedgerResponse | null }> | null = null;
  private inFlightBalance: Promise<BackendBalanceResponse | null> | null = null;
  private inFlightCatalog: Promise<StadiumCatalogResponse | null> | null = null;
  private inFlightConstruction: Promise<ConstructionStatus | null> | null = null;
  private inFlightTicketPrice: Promise<BackendTicketPriceResponse | null> | null = null;
  private inFlightSponsorOffers: Promise<SponsorMarketOffer[] | null> | null = null;
  private inFlightSponsorContracts: Promise<SponsorContract[] | null> | null = null;
  private inFlightLoansOffer: Promise<MatchdayLoanOffer | null> | null = null;
  private inFlightLoansDebt: Promise<AggregatedDebtResponse | null> | null = null;
  private inFlightEconomicSummary: Promise<EconomicSummaryResponse | null> | null = null;

  /**
   * Resetea el estado completo de economía al cambiar de usuario o club
   */
  resetEconomyState(): void {
    this.stadiumLoadedAt = this.ticketsLoadedAt = this.contractsLoadedAt = 0;
    this.contractsClubId = null;
    this.stadiumData.set(null);
    this.staffData.set(null);
    this.balanceData.set(null);
    this.ledgerData.set(null);
    this.loadedStadiumClubId.set(null);
    this.loadedStaffClubId.set(null);
    this.loadedEconomyClubId.set(null);
    this.loadedBalanceClubId.set(null);
    this.catalogData.set(null);
    this.constructionData.set(null);
    this.loadedCatalogClubId.set(null);
    this.loadedConstructionClubId.set(null);
    this.ticketPriceData.set(null);
    this.loadedTicketPriceClubId.set(null);
    this.sponsorOffers.set([]);
    this.sponsorContracts.set([]);
    this.sponsorOffersError.set(null);
    this.sponsorOffersStatus.set(null);
    this.sponsorContractsError.set(null);
    this.loadedSponsorsClubId.set(null);
    this.matchdayLoanOffer.set(null);
    this.activeMatchdayLoan.set(null);
    this.matchdayLoansHistory.set([]);
    this.aggregatedDebt.set(null);
    this.economicSummary.set(null);
    this.loansError.set(null);
    this.loadedLoansClubId.set(null);
    this.inFlightStadium = null;
    this.inFlightStaff = null;
    this.inFlightEconomy = null;
    this.inFlightBalance = null;
    this.inFlightCatalog = null;
    this.inFlightConstruction = null;
    this.inFlightTicketPrice = null;
    this.inFlightSponsorOffers = null;
    this.inFlightSponsorContracts = null;
    this.inFlightLoansOffer = null;
    this.inFlightLoansDebt = null;
    this.inFlightEconomicSummary = null;
  }

  /**
   * Fetch club stadium details from backend:
   * GET /api/clubs/{clubId}/stadium
   */
  async fetchClubStadium(
    clubId: string,
    forceRefresh: boolean = false
  ): Promise<BackendStadiumResponse | null> {
    if (!forceRefresh && this.loadedStadiumClubId() === clubId && this.stadiumData() && this.isFresh(this.stadiumLoadedAt)) {
      return this.stadiumData();
    }
    if (this.inFlightStadium) {
      return this.inFlightStadium;
    }

    this.isStadiumLoading.set(true);
    const requestPromise = (async () => {
      try {
        const data = await clubsApi.getClubStadium(clubId);
        this.stadiumData.set(data);
        this.loadedStadiumClubId.set(clubId);
        this.stadiumLoadedAt = Date.now();

        // Sync with user's club in squad.clubs signal
        this.squad.clubs.update((prev) =>
          prev.map((c) =>
            c.id === clubId || (c.isUser && (!c.id || c.id === 'club-user' || c.id === clubId))
              ? {
                  ...c,
                  id: clubId,
                  stadium: {
                    ...c.stadium,
                    capacity: data.capacity,
                    standsLevel: data.standsLevel,
                    ratingLevel: data.ratingLevel,
                    facilitiesRatingLevel: data.facilitiesRatingLevel,
                    createdAt: data.createdAt,
                    updatedAt: data.updatedAt,
                    version: data.version,
                    modules: {
                      parkingLevel: data.parkingLevel,
                      cafeteriaLevel: data.cafeteriaLevel,
                      merchandisingLevel: data.merchandisingLevel,
                      bathroomLevel: data.bathroomLevel ?? 1,
                    },
                  },
                }
              : c
          )
        );

        return data;
      } catch (err: any) {
        console.warn('Error fetching club stadium from backend:', err);
        this.ui.showNotification(
          err?.message || 'No se pudo sincronizar el estadio con el servidor',
          'info'
        );
        return null;
      } finally {
        this.isStadiumLoading.set(false);
        this.inFlightStadium = null;
      }
    })();

    this.inFlightStadium = requestPromise;
    return requestPromise;
  }

  /**
   * GET /api/clubs/{clubId}/stadium/upgrades/catalog
   * Consultar catálogo de mejoras de estadio: initialCapacity, maximumCapacity, upgrades[]
   */
  async fetchStadiumCatalog(
    clubId: string,
    forceRefresh: boolean = false
  ): Promise<StadiumCatalogResponse | null> {
    if (!forceRefresh && this.loadedCatalogClubId() === clubId && this.catalogData()) {
      return this.catalogData();
    }
    if (!forceRefresh && this.inFlightCatalog) {
      return this.inFlightCatalog;
    }

    this.isCatalogLoading.set(true);
    const requestPromise = (async () => {
      try {
        const data = await stadiumUpgradesApi.getCatalog(clubId);
        this.catalogData.set(data);
        this.loadedCatalogClubId.set(clubId);
        return data;
      } catch (err: any) {
        console.warn('Error fetching stadium upgrades catalog:', err);
        return null;
      } finally {
        this.isCatalogLoading.set(false);
        this.inFlightCatalog = null;
      }
    })();

    this.inFlightCatalog = requestPromise;
    return requestPromise;
  }

  /**
   * GET /api/clubs/{clubId}/stadium/upgrades/construction
   * Consultar obra activa del estadio.
   * Si desaparece una obra conocida, consulta el estado terminal de la operación y refresca estadio y saldo.
   */
  async fetchActiveConstruction(
    clubId: string,
    forceRefresh: boolean = false
  ): Promise<ConstructionStatus | null> {
    if (!forceRefresh && this.loadedConstructionClubId() === clubId && this.constructionData()) {
      return this.constructionData();
    }
    if (this.inFlightConstruction) {
      return this.inFlightConstruction;
    }

    const previousStatus = this.constructionData();
    const knownOpId = previousStatus?.operation?.id;

    this.isConstructionLoading.set(true);
    const requestPromise = (async () => {
      try {
        const data = await stadiumUpgradesApi.getActiveConstruction(clubId);

        // Si antes había una operación activa y ahora data.operation es null, consultar estado terminal
        if (knownOpId && !data.operation) {
          try {
            const terminalOp = await stadiumUpgradesApi.getOperation(clubId, knownOpId);
            if (terminalOp) {
              if (terminalOp.status === 'APPLIED') {
                this.ui.showNotification('¡La remodelación del estadio se ha completado con éxito!', 'success');
              } else if (terminalOp.status === 'FAILED') {
                this.ui.showNotification('La solicitud de remodelación ha fallado.', 'error');
              } else if (terminalOp.status === 'CANCELLED') {
                this.ui.showNotification('La obra de remodelación ha sido cancelada.', 'info');
              }
            }
          } catch (e) {
            console.warn('No se pudo obtener detalle de operación terminal desaparecida:', e);
          } finally {
            // Refrescar estadio y saldo de tesorería
            await Promise.all([
              this.fetchClubStadium(clubId, true),
              this.fetchClubBalance(clubId, true),
            ]);
          }
        }

        this.constructionData.set(data);
        this.loadedConstructionClubId.set(clubId);
        return data;
      } catch (err: any) {
        console.warn('Error fetching stadium active construction:', err);
        return null;
      } finally {
        this.isConstructionLoading.set(false);
        this.inFlightConstruction = null;
      }
    })();

    this.inFlightConstruction = requestPromise;
    return requestPromise;
  }

  /**
   * Fetch club staff details from backend:
   * GET /api/clubs/{clubId}/staff
   */
  async fetchClubStaff(
    clubId: string,
    forceRefresh: boolean = false
  ): Promise<BackendStaffMember[] | null> {
    if (!forceRefresh && this.loadedStaffClubId() === clubId && this.staffData()) {
      return this.staffData();
    }
    if (!forceRefresh && this.inFlightStaff) {
      return this.inFlightStaff;
    }

    this.isStaffLoading.set(true);
    const requestPromise = (async () => {
      try {
        const staffList = await clubsApi.getClubStaff(clubId);
        this.staffData.set(staffList);
        this.loadedStaffClubId.set(clubId);

        // Map backend staff entries to club star levels
        const gkMember = staffList.find(
          (m: any) => m.line === 'GOALKEEPER' || m.staffType === 'GOALKEEPER_COACH' || m.area === 'goalkeeping'
        );
        const fitMember = staffList.find(
          (m: any) => m.line === 'DEFENCE' || m.staffType === 'PHYSICAL_COACH' || m.area === 'fitness'
        );
        const techMember = staffList.find(
          (m: any) => m.line === 'MIDFIELD' || m.staffType === 'TECHNICAL_COACH' || m.area === 'technical'
        );
        const shootMember = staffList.find(
          (m: any) => m.line === 'FORWARD' || m.staffType === 'SHOOTING_COACH' || m.area === 'shooting'
        );

        this.squad.clubs.update((prev) =>
          prev.map((c) =>
            c.id === clubId
              ? {
                  ...c,
                  staff: {
                    goalkeeping: (gkMember as any)?.starRating ?? (gkMember as any)?.level ?? c.staff.goalkeeping,
                    fitness: (fitMember as any)?.starRating ?? (fitMember as any)?.level ?? c.staff.fitness,
                    technical: (techMember as any)?.starRating ?? (techMember as any)?.level ?? c.staff.technical,
                    shooting: (shootMember as any)?.starRating ?? (shootMember as any)?.level ?? c.staff.shooting,
                  },
                }
              : c
          )
        );

        return staffList;
      } catch (err: any) {
        console.warn('Error fetching club staff from backend:', err);
        this.ui.showNotification(
          err?.message || 'No se pudo sincronizar el staff técnico',
          'info'
        );
        return null;
      } finally {
        this.isStaffLoading.set(false);
        this.inFlightStaff = null;
      }
    })();

    this.inFlightStaff = requestPromise;
    return requestPromise;
  }

  /**
   * Fetch club economy balance from backend:
   * GET /api/clubs/{clubId}/economy/balance
   */
  async fetchClubBalance(
    clubId: string,
    forceRefresh: boolean = false
  ): Promise<BackendBalanceResponse | null> {
    if (!clubId || clubId === 'default') return null;

    if (!forceRefresh && this.loadedBalanceClubId() === clubId && this.balanceData()) {
      return this.balanceData();
    }

    if (this.inFlightBalance) {
      return this.inFlightBalance;
    }

    const reqPromise = (async () => {
      try {
        const data = await clubsApi.getClubEconomyBalance(clubId);
        this.balanceData.set(data);
        this.loadedBalanceClubId.set(clubId);

        // Backend amounts are in EUR (e.g. 45,000,000.00 EUR -> 45 M€)
        const availableM = +(data.availableBalance / 1_000_000).toFixed(3);
        const reservedM = +(data.reservedBalance / 1_000_000).toFixed(3);
        const totalM = +(data.totalBalance / 1_000_000).toFixed(3);

        this.squad.clubs.update((prev) => {
          const matchingIndex = prev.findIndex(
            (c) => c.id === clubId || c.isUser
          );
          let next: Club[];
          if (matchingIndex >= 0) {
            next = prev.map((c, idx) =>
              idx === matchingIndex
                ? {
                    ...c,
                    id: clubId,
                    isUser: true,
                    economy: {
                      ...c.economy,
                      availableBalance: availableM,
                      reservedBalance: reservedM,
                      totalBalance: totalM,
                      currency: data.currency,
                      version: data.version,
                    },
                  }
                : c
            );
          } else {
            const newClub: Club = {
              ...INITIAL_USER_CLUB,
              id: clubId,
              isUser: true,
              economy: {
                ...INITIAL_USER_CLUB.economy,
                availableBalance: availableM,
                reservedBalance: reservedM,
                totalBalance: totalM,
                currency: data.currency,
                version: data.version,
              },
            };
            next = [newClub, ...prev];
          }
          if (typeof clubStorage !== 'undefined') {
            try {
              clubStorage.setItem(`${STORAGE_KEY}_clubs`, JSON.stringify(next));
              clubStorage.setItem(`fm_real_club_balance_${clubId}`, JSON.stringify(data));
            } catch {}
          }
          return next;
        });

        return data;
      } catch (err: any) {
        console.warn('Error fetching club balance from backend:', err);
        return null;
      } finally {
        this.inFlightBalance = null;
      }
    })();

    this.inFlightBalance = reqPromise;
    return reqPromise;
  }

  /**
   * Fetch club ledger from backend:
   * GET /api/clubs/{clubId}/economy/ledger
   */
  async fetchClubLedger(
    clubId: string,
    page: number = 0,
    size: number = 20
  ): Promise<BackendLedgerResponse | null> {
    try {
      const data = await clubsApi.getClubEconomyLedger(clubId, page, size);
      if (page === 0 || !this.ledgerData()) {
        this.ledgerData.set(data);
      } else {
        const currentData = this.ledgerData() as any;
        const currentItems = currentData?.content || currentData?.items || currentData?.data || currentData?.entries || currentData?.transactions || currentData?.ledger || (Array.isArray(currentData) ? currentData : []);
        const newItems = (data as any)?.content || (data as any)?.items || (data as any)?.data || (data as any)?.entries || (data as any)?.transactions || (data as any)?.ledger || (Array.isArray(data) ? data : []);
        const mergedItems = [...currentItems, ...newItems];

        if (Array.isArray(currentData)) {
          this.ledgerData.set(mergedItems as any);
        } else if (currentData && typeof currentData === 'object') {
          const key = Array.isArray(currentData.content) ? 'content' :
                      Array.isArray(currentData.items) ? 'items' :
                      Array.isArray(currentData.data) ? 'data' :
                      Array.isArray(currentData.entries) ? 'entries' :
                      Array.isArray(currentData.transactions) ? 'transactions' :
                      Array.isArray(currentData.ledger) ? 'ledger' : 'content';
          this.ledgerData.set({
            ...currentData,
            [key]: mergedItems,
            totalPages: (data as any)?.totalPages ?? currentData.totalPages,
            last: (data as any)?.last ?? currentData.last,
          });
        } else {
          this.ledgerData.set(data);
        }
      }

      // Extraer los registros soportando content (Spring Page), array directo, items, data, ledger, etc.
      let rawItems: any[] = [];
      if (Array.isArray(data)) {
        rawItems = data;
      } else if (data && typeof data === 'object') {
        const obj = data as any;
        if (Array.isArray(obj.content)) {
          rawItems = obj.content;
        } else if (Array.isArray(obj.items)) {
          rawItems = obj.items;
        } else if (Array.isArray(obj.data)) {
          rawItems = obj.data;
        } else if (Array.isArray(obj.entries)) {
          rawItems = obj.entries;
        } else if (Array.isArray(obj.transactions)) {
          rawItems = obj.transactions;
        } else if (Array.isArray(obj.ledger)) {
          rawItems = obj.ledger;
        }
      }

      if (rawItems.length > 0) {
        const mappedEntries: LedgerEntry[] = rawItems.map((item: any, idx: number) => {
          const rawAmount = typeof item.amount === 'number' ? item.amount : Number(item.amount || 0);
          const amountM = rawAmount >= 10_000 ? +(rawAmount / 1_000_000).toFixed(3) : +rawAmount.toFixed(3);

          const direction = String(item.direction || item.type || '').toUpperCase();
          const isCredit =
            direction === 'CREDIT' ||
            direction === 'INGRESO' ||
            direction === 'INCOME' ||
            direction === 'DEPOSIT';

          return {
            id: String(
              item.id ||
              item.entryId ||
              item.transactionId ||
              item.reference ||
              item.sourceReference ||
              `led-${idx}-${Date.now()}`
            ),
            timestamp:
              item.occurredAt ||
              item.recordedAt ||
              item.createdAt ||
              item.timestamp ||
              item.date ||
              new Date().toISOString(),
            type: isCredit ? 'INGRESO' : 'GASTO',
            amount: Math.abs(amountM),
            concept:
              item.concept ||
              item.description ||
              item.title ||
              item.category ||
              'Movimiento contable',
            runningBalance: item.balanceAfter != null && Number.isFinite(Number(item.balanceAfter))
              ? Number(item.balanceAfter) / 1_000_000
              : null,
            direction: isCredit ? 'CREDIT' : 'DEBIT',
            category: item.category || item.conceptCategory || '',
            sourceType: item.sourceType || item.source || item.origin || 'CLUB',
            sourceReference: item.sourceReference || item.reference || '',
            occurredAt: item.occurredAt || item.recordedAt,
          };
        });

        this.squad.clubs.update((prev) => {
          const matchingIndex = prev.findIndex(
            (c) => c.id === clubId || (c.isUser && prev.every((x) => x.id !== clubId))
          );
          let next: Club[];
          if (matchingIndex >= 0) {
            const existingLedger = page === 0 ? [] : (prev[matchingIndex].economy?.ledger || []);
            const existingIds = new Set(existingLedger.map(e => e.id));
            const uniqueNewEntries = mappedEntries.filter(e => !existingIds.has(e.id));
            const combinedLedger = page === 0 ? mappedEntries : [...existingLedger, ...uniqueNewEntries];

            next = prev.map((c, idx) =>
              idx === matchingIndex
                ? {
                    ...c,
                    id: clubId,
                    isUser: true,
                    economy: {
                      ...c.economy,
                      ledger: combinedLedger,
                    },
                  }
                : c
            );
          } else {
            const newClub: Club = {
              ...INITIAL_USER_CLUB,
              id: clubId,
              isUser: true,
              economy: {
                ...INITIAL_USER_CLUB.economy,
                ledger: mappedEntries,
              },
            };
            next = [newClub, ...prev];
          }

          if (typeof clubStorage !== 'undefined') {
            try {
              clubStorage.setItem(`${STORAGE_KEY}_clubs`, JSON.stringify(next));
            } catch {}
          }
          return next;
        });
      }

      return data;
    } catch (err: any) {
      console.warn('Error fetching club ledger from backend:', err);
      return null;
    }
  }

  /**
   * Fetch all economy data (balance + ledger) from backend
   */
  async fetchClubEconomy(
    clubId: string,
    forceRefresh: boolean = false
  ): Promise<{ balance: BackendBalanceResponse | null; ledger: BackendLedgerResponse | null }> {
    if (!forceRefresh && this.loadedEconomyClubId() === clubId && this.balanceData()) {
      return { balance: this.balanceData(), ledger: this.ledgerData() };
    }
    if (!forceRefresh && this.inFlightEconomy) {
      return this.inFlightEconomy;
    }

    this.isEconomyLoading.set(true);
    const requestPromise = (async () => {
      try {
        const [balance, ledger] = await Promise.all([
          this.fetchClubBalance(clubId, forceRefresh),
          this.fetchClubLedger(clubId, 0, 20),
        ]);
        this.loadedEconomyClubId.set(clubId);
        return { balance, ledger };
      } catch (err: any) {
        console.warn('Error fetching club economy from backend:', err);
        this.ui.showNotification(
          err?.message || 'No se pudo sincronizar la economía con el servidor',
          'info'
        );
        return { balance: null, ledger: null };
      } finally {
        this.isEconomyLoading.set(false);
        this.inFlightEconomy = null;
      }
    })();

    this.inFlightEconomy = requestPromise;
    return requestPromise;
  }

  /**
   * GET /api/clubs/{clubId}/stadium/tickets
   * Consultar precio de entrada vigente y su versión propia
   */
  async fetchClubTicketPrice(
    clubId: string,
    forceRefresh: boolean = false
  ): Promise<BackendTicketPriceResponse | null> {
    if (!forceRefresh && this.loadedTicketPriceClubId() === clubId && this.ticketPriceData() && this.isFresh(this.ticketsLoadedAt)) {
      return this.ticketPriceData();
    }
    if (!forceRefresh && this.inFlightTicketPrice) {
      return this.inFlightTicketPrice;
    }

    this.isTicketPriceLoading.set(true);
    const requestPromise = (async () => {
      try {
        const data = await stadiumUpgradesApi.getTicketPrice(clubId);
        this.ticketPriceData.set(data);
        this.loadedTicketPriceClubId.set(clubId);
        this.ticketsLoadedAt = Date.now();

        // Actualizar en el club del usuario para sincronía global
        this.squad.clubs.update((prev) =>
          prev.map((c) =>
            c.id === clubId || (c.isUser && (!c.id || c.id === 'club-user' || c.id === clubId))
              ? {
                  ...c,
                  id: clubId,
                  stadium: {
                    ...c.stadium,
                    ticketPrice: data.ticketPrice,
                  },
                }
              : c
          )
        );
        return data;
      } catch (err: any) {
        console.warn('Error fetching stadium ticket price from backend:', err);
        return null;
      } finally {
        this.isTicketPriceLoading.set(false);
        this.inFlightTicketPrice = null;
      }
    })();

    this.inFlightTicketPrice = requestPromise;
    return requestPromise;
  }

  /**
   * PUT /api/clubs/{clubId}/stadium/tickets
   * Modificar el precio de las entradas vigentes.
   * Envía {"ticketPrice": number, "expectedVersion"?: number} con la versión leída.
   */
  async updateTicketPricing(
    ticketPrice: number,
    expectedVersion?: number
  ): Promise<BackendTicketPriceResponse | null> {
    const userC = this.squad.userClub();
    const clubId = userC.id || 'club-user';
    const versionToSend = expectedVersion !== undefined ? expectedVersion : this.ticketPriceData()?.version;

    try {
      const result = await stadiumUpgradesApi.updateTicketPrice(clubId, ticketPrice, versionToSend);
      this.ticketPriceData.set(result);
      this.loadedTicketPriceClubId.set(clubId);

      this.squad.clubs.update((prev) =>
        prev.map((c) =>
          c.id === clubId
            ? {
                ...c,
                stadium: {
                  ...c.stadium,
                  ticketPrice: result.ticketPrice,
                },
              }
            : c
        )
      );

      this.ui.showNotification(
        'Precio de entradas actualizado con éxito para el próximo partido',
        'success'
      );
      return result;
    } catch (err: any) {
      console.warn('Error al actualizar el precio de entradas:', err);
      const msg = err?.responseBody?.message || err?.message || 'Error al actualizar el precio de entradas';
      this.ui.showNotification(msg, 'error');

      // Si hay conflicto de versión (409), refrescar el precio actual desde el servidor
      if (err?.status === 409) {
        await this.fetchClubTicketPrice(clubId, true);
      }
      throw err;
    }
  }

  upgradeStadiumCapacity(newCapacity: number): {
    success: boolean;
    message: string;
  } {
    const userC = this.squad.userClub();
    const tier = STADIUM_TIERS.find((t) => t.capacity === newCapacity);
    if (!tier) return { success: false, message: 'Nivel de capacidad inválido' };

    if (userC.economy.availableBalance < tier.costM) {
      return {
        success: false,
        message: `Fondos insuficientes: requiere ${tier.costM} M€`,
      };
    }

    stadiumEconomyApi.upgradeCapacity(userC.id, newCapacity, tier.costM).catch((e) => {
      console.warn('Simulation fallback for capacity upgrade', e);
    });

    const newBalance = +(userC.economy.availableBalance - tier.costM).toFixed(3);
    const newLedgerEntry: LedgerEntry = {
      id: `led-stad-${Date.now()}`,
      timestamp: new Date().toISOString(),
      type: 'GASTO',
      amount: tier.costM,
      concept: `Ampliación del estadio a ${newCapacity.toLocaleString()} espectadores`,
      runningBalance: newBalance,
    };

    this.squad.clubs.update((prev) =>
      prev.map((c) =>
        c.id === userC.id
          ? {
              ...c,
              stadium: { ...c.stadium, capacity: newCapacity },
              economy: {
                ...c.economy,
                availableBalance: newBalance,
                ledger: [newLedgerEntry, ...c.economy.ledger],
              },
            }
          : c
      )
    );

    this.ui.showNotification(
      `Estadio ampliado con éxito a ${newCapacity.toLocaleString()} localidades`,
      'success'
    );
    return { success: true, message: 'Ampliación completada' };
  }

  upgradeCommercialModule(
    moduleType: 'parking' | 'cafeteria' | 'merchandising'
  ): {
    success: boolean;
    message: string;
  } {
    const userC = this.squad.userClub();
    const currentLevel =
      userC.stadium.modules[
        `${moduleType}Level` as keyof typeof userC.stadium.modules
      ];
    if (currentLevel >= 5)
      return { success: false, message: 'El módulo ya está al nivel máximo' };

    const nextTier = COMMERCIAL_MODULE_COSTS.find(
      (m) => m.level === currentLevel + 1
    );
    const cost = nextTier ? nextTier.costM : 2.5;
    if (userC.economy.availableBalance < cost) {
      return {
        success: false,
        message: `Fondos insuficientes: requiere ${cost} M€`,
      };
    }

    const newBalance = +(userC.economy.availableBalance - cost).toFixed(3);
    stadiumEconomyApi.upgradeModule(userC.id, moduleType, currentLevel + 1, cost).catch((e) => {
      console.warn('Simulation fallback for module upgrade', e);
    });

    const newLedgerEntry: LedgerEntry = {
      id: `led-mod-${Date.now()}`,
      timestamp: new Date().toISOString(),
      type: 'GASTO',
      amount: cost,
      concept: `Mejora de módulo de ${moduleType} a Nivel ${currentLevel + 1}`,
      runningBalance: newBalance,
    };

    this.squad.clubs.update((prev) =>
      prev.map((c) =>
        c.id === userC.id
          ? {
              ...c,
              stadium: {
                ...c.stadium,
                modules: {
                  ...c.stadium.modules,
                  [`${moduleType}Level`]: currentLevel + 1,
                },
              },
              economy: {
                ...c.economy,
                availableBalance: newBalance,
                ledger: [newLedgerEntry, ...c.economy.ledger],
              },
            }
          : c
      )
    );

    this.ui.showNotification(
      `Módulo de ${moduleType} ampliado a Nivel ${currentLevel + 1}`,
      'success'
    );
    return { success: true, message: 'Mejora completada' };
  }

  requestBankLoan(
    amountM: number,
    termSeasons: number
  ): { success: boolean; message: string } {
    const userC = this.squad.userClub();
    if (amountM > 25)
      return {
        success: false,
        message: 'El préstamo máximo permitido es de 25 M€',
      };

    const newBalance = +(userC.economy.availableBalance + amountM).toFixed(3);
    const installment = +((amountM * 1.06) / termSeasons).toFixed(3);

    stadiumEconomyApi.requestLoan(userC.id, amountM, termSeasons).catch((e) => {
      console.warn('Simulation fallback for loan request', e);
    });

    const loan: BankLoan = {
      id: `loan-${Date.now()}`,
      principal: amountM,
      remaining: +(amountM * 1.06).toFixed(3),
      annualInterestRate: 0.06,
      termSeasons,
      seasonalInstallment: installment,
      takenOnSeason: 1,
    };

    const newLedgerEntry: LedgerEntry = {
      id: `led-loan-${Date.now()}`,
      timestamp: new Date().toISOString(),
      type: 'INGRESO',
      amount: amountM,
      concept: `Concesión préstamo bancario (${amountM} M€ a ${termSeasons} temporadas, 6% anual)`,
      runningBalance: newBalance,
    };

    this.squad.clubs.update((prev) =>
      prev.map((c) =>
        c.id === userC.id
          ? {
              ...c,
              economy: {
                ...c.economy,
                availableBalance: newBalance,
                loans: [...c.economy.loans, loan],
                ledger: [newLedgerEntry, ...c.economy.ledger],
              },
            }
          : c
      )
    );

    this.ui.showNotification(
      `Préstamo concedido: +${amountM} M€ transferidos a tu cuenta`,
      'success'
    );
    return { success: true, message: 'Préstamo activado con éxito' };
  }

  payMonthlySubscription(): { success: boolean; message: string } {
    const userC = this.squad.userClub();
    if (userC.economy.subscriptionCredits < 100) {
      return {
        success: false,
        message: 'Créditos insuficientes para abonar la cuota mensual',
      };
    }

    stadiumEconomyApi.paySubscription(userC.id, 100).catch((e) => {
      console.warn('Simulation fallback for subscription payment', e);
    });

    this.squad.clubs.update((prev) =>
      prev.map((c) =>
        c.id === userC.id
          ? {
              ...c,
              economy: {
                ...c.economy,
                subscriptionCredits: c.economy.subscriptionCredits - 100,
              },
            }
          : c
      )
    );

    this.ui.showNotification(
      'Cuota mensual de suscripción abonada (-100 CR)',
      'info'
    );
    return { success: true, message: 'Cuota abonada' };
  }

  topUpCredits(amountCr: number): { success: boolean; message: string } {
    const userC = this.squad.userClub();
    this.squad.clubs.update((prev) =>
      prev.map((c) =>
        c.id === userC.id
          ? {
              ...c,
              economy: {
                ...c.economy,
                subscriptionCredits: c.economy.subscriptionCredits + amountCr,
              },
            }
          : c
      )
    );

    this.ui.showNotification(
      `Wallet Mánager recargado con +${amountCr} CR`,
      'success'
    );
    return { success: true, message: `Recargados ${amountCr} CR` };
  }

  // ==========================================
  // SPONSORS (PATROCINIOS DEL CLUB)
  // ==========================================

  /**
   * GET /api/clubs/{clubId}/economy/sponsors/market
   * Carga las ofertas vigentes de patrocinio para la jornada en curso.
   * Maneja respuesta vacía [] y error 503 con reintento.
   */
  async fetchSponsorOffers(
    clubId: string,
    forceRefresh: boolean = false
  ): Promise<SponsorMarketOffer[]> {
    if (!forceRefresh && this.inFlightSponsorOffers) {
      return (await this.inFlightSponsorOffers) || [];
    }

    this.isSponsorOffersLoading.set(true);
    this.sponsorOffersError.set(null);
    this.sponsorOffersStatus.set(null);

    const requestPromise = (async () => {
      try {
        const offers = await sponsorsApi.getMarketOffers(clubId);
        this.sponsorOffers.set(offers || []);
        this.sponsorOffersStatus.set(200);
        return offers || [];
      } catch (err: any) {
        console.warn('Error fetching sponsor market offers:', err);
        const status = err?.status || err?._httpStatus;
        this.sponsorOffersStatus.set(status || 500);

        if (status === 503) {
          this.sponsorOffersError.set('El mercado de patrocinios no está disponible temporalmente. Inténtalo de nuevo.');
        } else {
          this.sponsorOffersError.set(err?.message || 'Error al consultar las ofertas de patrocinio.');
        }
        return [];
      } finally {
        this.isSponsorOffersLoading.set(false);
        this.inFlightSponsorOffers = null;
      }
    })();

    this.inFlightSponsorOffers = requestPromise;
    return requestPromise;
  }

  /**
   * GET /api/clubs/{clubId}/economy/sponsors/contracts
   * Carga todos los contratos del club (paginado completo) para asegurar
   * que se incluyan todos los contratos ACTIVE y PENDING_ACTIVATION.
   */
  async fetchSponsorContracts(
    clubId: string,
    forceRefresh: boolean = false
  ): Promise<SponsorContract[]> {
    if (!forceRefresh && this.contractsClubId === clubId && this.isFresh(this.contractsLoadedAt)) {
      return this.sponsorContracts();
    }
    if (!forceRefresh && this.inFlightSponsorContracts) {
      return (await this.inFlightSponsorContracts) || [];
    }

    this.isSponsorContractsLoading.set(true);
    this.sponsorContractsError.set(null);

    const requestPromise = (async () => {
      try {
        const allContracts = await sponsorsApi.getAllContracts(clubId);
        this.sponsorContracts.set(allContracts);
        this.contractsLoadedAt = Date.now();
        this.contractsClubId = clubId;
        this.loadedSponsorsClubId.set(clubId);
        return allContracts;
      } catch (err: any) {
        console.warn('Error fetching sponsor contracts:', err);
        this.sponsorContractsError.set(err?.message || 'Error al consultar los contratos de patrocinio.');
        return [];
      } finally {
        this.isSponsorContractsLoading.set(false);
        this.inFlightSponsorContracts = null;
      }
    })();

    this.inFlightSponsorContracts = requestPromise;
    return requestPromise;
  }

  /**
   * POST /api/clubs/{clubId}/economy/sponsors/contracts
   * Contrata una oferta de patrocinio enviando X-Request-Id.
   */
  async contractSponsorOffer(
    clubId: string,
    offerId: string,
    requestId: string
  ): Promise<SponsorContract> {
    try {
      const contract = await sponsorsApi.contractOffer(clubId, offerId, requestId);

      // Si la respuesta es 202 con PENDING_ACTIVATION
      if (contract.status === 'PENDING_ACTIVATION') {
        this.ui.showNotification(
          `Patrocinio con ${contract.sponsorName || 'el sponsor'} registrado. Pendiente de activación para la próxima jornada.`,
          'info'
        );
      } else {
        this.ui.showNotification(
          `¡Contrato firmado con éxito con ${contract.sponsorName || 'el sponsor'}!`,
          'success'
        );
      }

      // Recargar contratos, ofertas y saldo tras contratar
      await Promise.all([
        this.fetchSponsorContracts(clubId, true),
        this.fetchSponsorOffers(clubId, true),
        this.fetchClubBalance(clubId, true),
      ]);

      return contract;
    } catch (err: any) {
      console.warn('Error contracting sponsor offer:', err);

      // Si el error es 409 (conflicto, tipo ya ocupado, oferta no disponible)
      if (err?.status === 409) {
        this.ui.showNotification(
          err?.message || 'La oferta ya no está disponible o el tipo de patrocinio ya ha sido ocupado.',
          'error'
        );
        // Sincronizar datos para mostrar el estado actual
        await Promise.all([
          this.fetchSponsorContracts(clubId, true),
          this.fetchSponsorOffers(clubId, true),
        ]);
      } else if (err?.status === 503) {
        this.ui.showNotification(
          'El servicio de contratación no está disponible temporalmente. Puedes reintentar la operación.',
          'error'
        );
      } else {
        this.ui.showNotification(
          err?.message || 'No se pudo formalizar el contrato de patrocinio.',
          'error'
        );
      }
      throw err;
    }
  }

  // =============================================================
  // CRÉDITOS POR JORNADA (MATCHDAY LOANS)
  // =============================================================

  /**
   * GET /api/clubs/{clubId}/economy/loans/offer
   * Consulta la oferta autoritativa de crédito por jornada
   */
  async fetchMatchdayLoanOffer(
    clubId: string,
    forceRefresh: boolean = false
  ): Promise<MatchdayLoanOffer | null> {
    if (!forceRefresh && this.inFlightLoansOffer) {
      return await this.inFlightLoansOffer;
    }
    if (!forceRefresh && this.loadedLoansClubId() === clubId && this.matchdayLoanOffer()) {
      return this.matchdayLoanOffer();
    }

    this.isLoansLoading.set(true);
    this.loansError.set(null);

    const requestPromise = (async () => {
      try {
        const offer = await loansApi.getOffer(clubId);
        this.matchdayLoanOffer.set(offer);
        if (offer.activeLoan) {
          this.activeMatchdayLoan.set(offer.activeLoan);
        }
        this.loadedLoansClubId.set(clubId);
        return offer;
      } catch (err: any) {
        console.warn('Error fetching matchday loan offer:', err);
        const errorMsg =
          err?.responseBody?.detail ||
          err?.responseBody?.message ||
          err?.message ||
          'Error al consultar la oferta de crédito por jornada.';
        this.loansError.set(errorMsg);
        return null;
      } finally {
        this.isLoansLoading.set(false);
        this.inFlightLoansOffer = null;
      }
    })();

    this.inFlightLoansOffer = requestPromise;
    return requestPromise;
  }

  /**
   * GET /api/clubs/{clubId}/economy/loans?page={page}&size={size}
   * Consulta el historial paginado de préstamos del club
   */
  async fetchMatchdayLoansHistory(
    clubId: string,
    page: number = 0,
    size: number = 20
  ): Promise<MatchdayLoanItem[]> {
    try {
      const history = await loansApi.getHistory(clubId, page, size);
      this.matchdayLoansHistory.set(history);
      return history;
    } catch (err: any) {
      console.warn('Error fetching matchday loans history:', err);
      return [];
    }
  }

  /**
   * GET /api/clubs/{clubId}/economy/loans/debt
   * Consulta la deuda total agregada (créditos por jornada + préstamos antiguos)
   */
  async fetchAggregatedDebt(
    clubId: string,
    forceRefresh: boolean = false
  ): Promise<AggregatedDebtResponse | null> {
    if (!forceRefresh && this.inFlightLoansDebt) {
      return await this.inFlightLoansDebt;
    }

    const requestPromise = (async () => {
      try {
        const debt = await loansApi.getAggregatedDebt(clubId);
        this.aggregatedDebt.set(debt);
        return debt;
      } catch (err: any) {
        console.warn('Error fetching aggregated debt:', err);
        return null;
      } finally {
        this.inFlightLoansDebt = null;
      }
    })();

    this.inFlightLoansDebt = requestPromise;
    return requestPromise;
  }

  /**
   * GET /api/clubs/{clubId}/economy/summary
   * Consulta el resumen económico del club con saldos oficializados
   */
  async fetchEconomicSummary(
    clubId: string,
    forceRefresh: boolean = false
  ): Promise<EconomicSummaryResponse | null> {
    if (!forceRefresh && this.inFlightEconomicSummary) {
      return await this.inFlightEconomicSummary;
    }

    const requestPromise = (async () => {
      try {
        const summary = await loansApi.getEconomicSummary(clubId);
        this.economicSummary.set(summary);
        return summary;
      } catch (err: any) {
        console.warn('Error fetching economic summary:', err);
        return null;
      } finally {
        this.inFlightEconomicSummary = null;
      }
    })();

    this.inFlightEconomicSummary = requestPromise;
    return requestPromise;
  }

  /**
   * POST /api/clubs/{clubId}/economy/loans
   * Contrata un préstamo por jornada enviando cabecera idempotente X-Request-Id.
   */
  async contractMatchdayLoan(
    clubId: string,
    principal: number,
    requestId: string
  ): Promise<ActiveMatchdayLoan> {
    this.isLoansLoading.set(true);
    this.loansError.set(null);

    try {
      const activeLoan = await loansApi.contractLoan(
        clubId,
        principal,
        requestId
      );
      this.activeMatchdayLoan.set(activeLoan);

      this.ui.showNotification(
        `Crédito concedido: ${principal.toLocaleString('es-ES')} € ingresados en la tesorería del club.`,
        'success'
      );

      // Tras contratación exitosa, refrescar oferta, deuda agregada, historial y balances
      await Promise.all([
        this.fetchMatchdayLoanOffer(clubId, true),
        this.fetchAggregatedDebt(clubId, true),
        this.fetchMatchdayLoansHistory(clubId, 0, 20),
        this.fetchEconomicSummary(clubId, true),
        this.fetchClubBalance(clubId, true),
      ]);

      return activeLoan;
    } catch (err: any) {
      console.warn('Error contracting matchday loan:', err);

      const errorDetail =
        err?.responseBody?.detail ||
        err?.responseBody?.message ||
        err?.message ||
        'Error al formalizar la contratación del crédito.';

      if (err?.status === 409) {
        this.ui.showNotification(
          `Conflicto en la contratación (409): ${errorDetail}`,
          'error'
        );
        // En conflicto (409), refrescar los datos del servidor para sincronizar estado
        await Promise.all([
          this.fetchMatchdayLoanOffer(clubId, true),
          this.fetchAggregatedDebt(clubId, true),
        ]);
      } else {
        this.ui.showNotification(errorDetail, 'error');
      }

      this.loansError.set(errorDetail);
      throw err;
    } finally {
      this.isLoansLoading.set(false);
    }
  }

  /**
   * POST /api/clubs/{clubId}/economy/loans/{loanId}/repay
   * Amortiza capital anticipado sin comisiones enviando cabecera idempotente X-Request-Id.
   */
  async repayMatchdayLoan(
    clubId: string,
    loanId: string,
    amount: number,
    requestId: string
  ): Promise<ActiveMatchdayLoan> {
    this.isLoansLoading.set(true);
    this.loansError.set(null);

    try {
      const updatedLoan = await loansApi.repayLoan(
        clubId,
        loanId,
        amount,
        requestId
      );
      this.activeMatchdayLoan.set(updatedLoan);

      this.ui.showNotification(
        `Amortización de ${amount.toLocaleString('es-ES')} € ejecutada con éxito sin comisión.`,
        'success'
      );

      // Tras amortización exitosa, refrescar oferta, deuda, historial y saldos
      await Promise.all([
        this.fetchMatchdayLoanOffer(clubId, true),
        this.fetchAggregatedDebt(clubId, true),
        this.fetchMatchdayLoansHistory(clubId, 0, 20),
        this.fetchEconomicSummary(clubId, true),
        this.fetchClubBalance(clubId, true),
      ]);

      return updatedLoan;
    } catch (err: any) {
      console.warn('Error repaying matchday loan:', err);

      const errorDetail =
        err?.responseBody?.detail ||
        err?.responseBody?.message ||
        err?.message ||
        'Error al procesar la amortización de capital.';

      if (err?.status === 409) {
        this.ui.showNotification(
          `Conflicto en amortización (409): ${errorDetail}`,
          'error'
        );
        await Promise.all([
          this.fetchMatchdayLoanOffer(clubId, true),
          this.fetchAggregatedDebt(clubId, true),
          this.fetchClubBalance(clubId, true),
        ]);
      } else {
        this.ui.showNotification(errorDetail, 'error');
      }

      this.loansError.set(errorDetail);
      throw err;
    } finally {
      this.isLoansLoading.set(false);
    }
  }
}
